import type WebSocket from 'ws';
import { createAcpManager, type AcpManager } from '../agents/acp/create-acp-manager.js';
import type { E2eCertificate } from '../e2e-certificates/certificates.js';
import { createCliE2eeRuntime, type CliE2eeRuntime } from '../lib/e2ee/index.js';
import { createBridgeAccessState } from '../mcp/bridge-access/index.js';
import type { LogFn } from '../log.js';
import { createAgentCapabilitiesReporter } from './create-bridge-agent-capabilities-reporter.js';
import type { BridgeConnectionTokens } from './bridge-connection-runtime-types.js';

export async function createBridgeAccessAndAcp(options: {
  apiUrl: string;
  workspaceId: string;
  tokens: BridgeConnectionTokens;
  e2eCertificate?: E2eCertificate;
  getWs: () => WebSocket | null;
  log: LogFn;
}): Promise<{
  e2ee: CliE2eeRuntime | undefined;
  acpManager: AcpManager;
  bridgeAccessPromise: ReturnType<typeof createBridgeAccessState>;
}> {
  const { apiUrl, workspaceId, tokens, getWs, log } = options;
  const e2ee = options.e2eCertificate ? createCliE2eeRuntime(options.e2eCertificate) : undefined;

  const bridgeAccessRef: { current: Awaited<ReturnType<typeof createBridgeAccessState>> | null } = {
    current: null,
  };
  const bridgeAccessPromise = createBridgeAccessState({
    cloudApiBaseUrl: apiUrl,
    getCloudAccessToken: () => tokens.accessToken,
    e2ee,
  }).then((bridgeAccess) => {
    bridgeAccessRef.current = bridgeAccess;
    return bridgeAccess;
  });

  const sendAgentCapabilitiesToBridge = createAgentCapabilitiesReporter({ workspaceId, getWs });
  const acpManager = await createAcpManager({
    log,
    getBridgeAccessPort: () => bridgeAccessRef.current?.getPort() ?? 0,
    reportAgentCapabilities: sendAgentCapabilitiesToBridge,
  });

  return { e2ee, acpManager, bridgeAccessPromise };
}
